import React from "react";
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { getFormattedDateTime } from "utils/helper";
import AlertBox from "./alertBox";
import { getEnrichedRows } from "./helper";
import { Refresh } from "@mui/icons-material";

const EMRAppointmentLoadDetailsDialog = ({
  open,
  onClose,
  rows = [],
  loading = false,
  onRefresh,
}) => {
  const enrichedRows = getEnrichedRows(rows);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
    },
    {
      field: "job_name",
      headerName: "Job Name",
      flex: 1.4,
      minWidth: 260,
    },
    {
      field: "job_type",
      headerName: "Job Type",
      width: 130,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const completed = params.value === "Completed";
        return (
          <Chip
            label={params.value || "-"}
            size="small"
            icon={
              completed ? (
                <CheckCircleOutlineIcon fontSize="small" />
              ) : (
                <AccessTimeIcon fontSize="small" />
              )
            }
            sx={{
              fontWeight: 600,
              backgroundColor: completed ? "#e8f5e9" : "#fff4e5",
              color: completed ? "#1b5e20" : "#8a4b00",
              border: `1px solid ${completed ? "#a5d6a7" : "#ffcc80"}`,
            }}
          />
        );
      },
    },
    {
      field: "job_run_date",
      headerName: "Job Run Date",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const value = params.value;
        return value ? getFormattedDateTime(value) : "-";
      },
      sortComparator: (v1, v2) => {
        const d1 = v1 ? dayjs(v1).valueOf() : 0;
        const d2 = v2 ? dayjs(v2).valueOf() : 0;
        return d1 - d2;
      },
    },
    {
      field: "ageInHours",
      headerName: "Age",
      width: 130,
      renderCell: (params) => {
        const hours = Number(params.value || 0);
        const isOld = params.row?.isOlderThan1Day;

        return (
          <Typography
            fontWeight={700}
            color={isOld ? "error.main" : "success.main"}
          >
            {hours >= 24
              ? `${Math.floor(hours / 24)}d ${Math.round(hours % 24)}h`
              : `${Math.round(hours)}h`}
          </Typography>
        );
      },
      sortComparator: (v1, v2) => Number(v1 || 0) - Number(v2 || 0),
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 1.8,
      minWidth: 360,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pr: 6 }}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box display={"flex"} alignItems={"center"} gap={2}>
            <Box>
              <Typography fontWeight={700} fontSize={20}>
                EMR Appointment Load Details
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Recent appointment sync jobs
              </Typography>
            </Box>
            <IconButton color="primary" disabled={loading} onClick={onRefresh}>
              <Refresh />
            </IconButton>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <AlertBox rows={rows} />

        <DataGrid
          rows={enrichedRows}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "job_run_date", sort: "desc" }],
            },
          }}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          sx={{
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              py: 1,
            },
            "& .stale-load-row": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
            "& .stale-load-row:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.14)",
            },
          }}
          getRowClassName={(params) =>
            params.row?.isOlderThan1Day ? "stale-load-row" : ""
          }
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EMRAppointmentLoadDetailsDialog;
